import type { Point } from 'pixi.js';
import { Container, Sprite } from 'pixi.js';
import Lerper from '../../../utils/Lerper';
import { DioramaMaskView } from './DioramaMaskView';
import type { DioramaPieceConfig } from '../../../app/state/diorama/dioramaState';
import MathUtils from '../../../utils/MathUtils';
import { createDioramaPieceCompletedAnimation } from './createDioramaPieceCompletedAnimation';
import { soundManager } from '../../../app/SoundManager';

export class DioramaPieceView extends Container {
  private static canPlayHitSound = true;

  private readonly hitWrapper = new Container().props({ parent: this });

  private readonly shadowSprite: Sprite | null;

  private readonly maskView: DioramaMaskView | null;

  private readonly hitScaleLerper: Lerper;

  private currentProgress: number;

  private visualProgress: number;

  constructor(
    private readonly config: DioramaPieceConfig,
    initProgress: number,
  ) {
    super();

    this.hitScaleLerper = new Lerper({
      displayObject: this,
      initValue: 1,
      onChange: (value): void => {
        void this.hitWrapper.scale.set(value);
      },
    });

    this.currentProgress = initProgress;
    this.visualProgress = initProgress;

    this.shadowSprite = this.initShadow();

    new Sprite().props({
      texture: config.texture,
      anchor: 0.5,
      parent: this.hitWrapper,
    });

    this.maskView = this.initMask();
  }

  public isCompleted(): boolean {
    return this.currentProgress >= this.config.price;
  }

  private initMask(): DioramaMaskView | null {
    return this.isCompleted()
      ? null
      : new DioramaMaskView(this.config)
          .props({
            parent: this.hitWrapper,
          })
          .setProgress(this.currentProgress);
  }

  private initShadow(): Sprite | null {
    if (!this.config.shadow) return null;

    return new Sprite().props({
      ...this.config.shadow,
      parent: this.hitWrapper,
      anchor: 0.5,
      alpha: this.isCompleted() ? 1 : 0,
    });
  }

  public getParticleTarget(): Point {
    return this.maskView?.getParticleTarget() ?? this.getGlobalPosition();
  }

  public increaseProgress(): void {
    if (this.currentProgress >= this.config.price) return;
    this.currentProgress += 1;
  }

  public async increaseProgressVisually(): Promise<void> {
    if (this.visualProgress >= this.config.price) return;
    if (this.visualProgress === 0) {
      this.animateShow();
    }

    this.visualProgress += 1;

    void this.updateDisplayedValue(this.visualProgress);

    if (DioramaPieceView.canPlayHitSound) {
      DioramaPieceView.canPlayHitSound = false;
      soundManager.play('Diorama_Hit');
      this.wait(20).then(
        () => {
          DioramaPieceView.canPlayHitSound = true;
        },
        () => {},
      );
    }

    this.hitScaleLerper.setTarget(MathUtils.getRandomValueInRange(1.05, 1.1), true);
    this.hitScaleLerper.setTarget(1);
  }

  public animateShow(): this {
    if (this.scaleX !== 0) return this;
    this.tweenTo({ scaleX: 1, scaleY: 1 }, 300, 'appear1', true);
    soundManager.play('Diorama_Piece_Appearing');
    return this;
  }

  public prepareForShow(): this {
    this.scale.set(0);
    return this;
  }

  private async updateDisplayedValue(value: number): Promise<void> {
    this.maskView?.setProgress(value);
    if (value < this.config.price) return;
    this.tweenTo({ scaleX: 1.05, scaleY: 1.05 }, 100, 'cubicInOut', true, 0, 0, true);

    await this.wait(100);

    this.maskView?.tweenTo({ alpha: 0 }, 200, 'sinIn', true);
    this.shadowSprite?.tweenTo({ alpha: 1 }, 200, 'sinOut', true);
    createDioramaPieceCompletedAnimation().props({
      parent: this,
    });
    soundManager.play('Diorama_Piece_Completed');
  }
}
